import React, { useState, useEffect } from "react";
import lineShape from "../../assets/image/card-shape.svg";
import { FiCheck } from "react-icons/fi";
import apiService from "../../actions/apiService";

const Backdrop = ({ onClick }) => (
  <div
    className="fixed inset-0 bg-black/30 z-[110]"
    onClick={onClick}
    aria-hidden
  />
);

const ModalContainer = ({ children }) => (
  <div className="fixed mt-[10px] mb-[10px] inset-0 z-[110] flex items-center justify-center">
    <div className="w-full rounded-[12px] overflow-y-auto h-full max-w-[852px] bg-white overflow-hidden px-5 md:px-16 py-5 md:py-8">
      {children}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="font-rubik text-big-price md:text-big-price-d text-esdarkGray !font-[500]">
    {children}
  </h3>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white border w-full border-[#DCEDFF] rounded-[12px] p-5 ${className}`}
  >
    {children}
  </div>
);

// Map transaction status to the same labels/colors used in the admin list
const getStatusBadge = (tx) => {
  if (!tx) return { text: "", className: "" };

  // Cancelled
  if (tx.status === "cancelled") {
    const cancelledText =
      tx.payment_cancelled_reason === "item_not_authentication"
        ? "Cancelled - Authentication Failed"
        : "Cancelled";
    return {
      text: cancelledText,
      className: "bg-gray-200 text-gray-700",
    };
  }

  // Admin released funds
  if (tx.admin_released) {
    return { text: "Funds released", className: "bg-[#CBECF9] text-[#036]" };
  }

  // Confirmations
  if (tx.buyer_confirmed && tx.seller_confirmed) {
    return {
      text: "Both parties confirmed",
      className: "bg-[#B9FFCC] text-[#16653F]",
    };
  }
  if (tx.buyer_confirmed) {
    return {
      text: "Buyer confirmed, awaiting seller confirmation",
      className: "bg-[#FFC49F] text-[#D35100]",
    };
  }
  if (tx.seller_confirmed) {
    return {
      text: "Seller confirmed, awaiting buyer confirmation",
      className: "bg-[#FFC49F] text-[#D35100]",
    };
  }

  // Default
  return {
    text: "Verification in progress",
    className: "bg-[#CBECF9] text-[#036]",
  };
};

const TransactionPreviewModal = ({ open, onClose, transactionId }) => {
  const [transactionData, setTransactionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null); // Track which recipient is sending (null, "seller", or "buyer")
  const [sentReminder, setSentReminder] = useState(null); // Track which recipient email was sent (null, "seller", or "buyer")
  const [sellerStats, setSellerStats] = useState({
    totalSales: 0,
    totalSoldValue: 0,
    sellerRating: 0,
  });
  const [buyerStats, setBuyerStats] = useState({
    totalPurchases: 0,
    sellerRating: 0,
    trustScore: 0,
  });

  // Normalize sale and escrow amounts so the modal shows the actual paid values
  const salePrice = React.useMemo(() => {
    if (!transactionData) return 0;
    const amount =
      transactionData.buyer_paid_amount ??
      transactionData.total_amount ??
      transactionData.amount ??
      transactionData.item?.price ??
      0;
    const numericAmount =
      typeof amount === "number" ? amount : Number(amount) || 0;
    return numericAmount;
  }, [transactionData]);

  const escrowAmount = React.useMemo(() => {
    if (!transactionData) return 0;
    const status = transactionData.status?.toLowerCase();
    const sellerPayout =
      typeof transactionData.seller_payout === "number"
        ? transactionData.seller_payout
        : Number(transactionData.seller_payout) || 0;
    const escrow =
      typeof transactionData.escrow_amount === "number"
        ? transactionData.escrow_amount
        : Number(transactionData.escrow_amount) || null;

    // If released, show the amount that was actually paid out
    if (status === "released") return sellerPayout;

    // Otherwise show the amount currently held in escrow if available
    if (escrow != null) return escrow;

    // Fallback to payout so the card never shows 0 when data exists
    return sellerPayout;
  }, [transactionData]);

  const statusBadge = React.useMemo(
    () => getStatusBadge(transactionData),
    [transactionData]
  );

  useEffect(() => {
    const fetchTransaction = async () => {
      if (!open || !transactionId) return;

      setLoading(true);
      setError(null);
      try {
        const res = await apiService.get(
          `/admin/transactions/${transactionId}`
        );
        if (res?.success) {
          setTransactionData(res.data.transaction);
        } else {
          setError(res?.error || "Failed to load transaction data");
        }
      } catch (err) {
        console.error("Error fetching transaction:", err);
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();
  }, [open, transactionId]);

  // Fetch seller/buyer stats similar to buyer/seller dashboards
  useEffect(() => {
    const fetchStats = async () => {
      if (!transactionData) return;

      const sellerId =
        transactionData.seller_id ||
        transactionData.sellerId ||
        transactionData.seller?.id;
      const buyerId =
        transactionData.buyer_id ||
        transactionData.buyerId ||
        transactionData.buyer?.id;

      try {
        if (sellerId) {
          const res = await apiService.get("/transactions/seller-stats", {
            seller_id: sellerId,
          });
          if (res?.success && res.data) {
            setSellerStats({
              totalSales: res.data.totalSales || 0,
              totalSoldValue: res.data.totalSoldValue || 0,
              sellerRating:
                typeof res.data.sellerRating === "number"
                  ? res.data.sellerRating
                  : 0,
            });
          }
        }
      } catch (_) {
        // ignore and keep defaults
      }

      try {
        if (buyerId) {
          const res = await apiService.get("/transactions/buyer-stats", {
            buyer_id: buyerId,
          });
          if (res?.success && res.data) {
            setBuyerStats({
              totalPurchases: res.data.totalPurchases || 0,
              sellerRating:
                typeof res.data.sellerRating === "number"
                  ? res.data.sellerRating
                  : 0,
              trustScore:
                typeof res.data.trustScore === "number"
                  ? res.data.trustScore
                  : 0,
            });
          }
        }
      } catch (_) {
        // ignore and keep defaults
      }
    };

    fetchStats();
  }, [transactionData]);

  // Reset data when modal closes
  useEffect(() => {
    if (!open) {
      setTransactionData(null);
      setError(null);
      setSendingReminder(null);
      setSentReminder(null);
    }
  }, [open]);

  const handleSendReminder = async (recipient) => {
    if (!transactionData || !transactionId || !recipient) return;

    setSendingReminder(recipient);
    setSentReminder(null);
    try {
      const res = await apiService.post(
        `/admin/transactions/${transactionId}/send-reminder`,
        { recipient }
      );
      if (res?.success) {
        // Show "Email sent" on button
        setSendingReminder(null);
        setSentReminder(recipient);
        // Reset button text after 2 seconds
        setTimeout(() => {
          setSentReminder(null);
        }, 2000);
      } else {
        setSendingReminder(null);
      }
    } catch (err) {
      console.error("Error sending reminder:", err);
      setSendingReminder(null);
    }
  };

  if (!open) return null;
  return (
    <>
      <Backdrop onClick={onClose} />
      <ModalContainer>
        <div className="pb-8 flex items-center justify-between">
          <div>
            <h3 className="text-page-title font-rubik md:text-page-title-d font-semibold text-[#1E1E1E]">
              Transaction management
            </h3>
            <p className="text-[18px] font-[400] opacity-50 mt-4">
              Manage transaction and status of your listing.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="h-9 w-9 rounded-[8px] hover:bg-[#F4F6F8] grid place-items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="#1E1E1E"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="py-8 text-center">
            <div className="text-sm text-gray-500">
              Loading transaction data...
            </div>
          </div>
        )}

        {!loading && !transactionData && (
          <div className="py-8 text-center">
            <div className="text-sm text-gray-500">
              No transaction data found.
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="py-8">
            <div className="bg-red-50 border border-red-200 rounded-[8px] p-4">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          </div>
        )}

        {/* Content - only show if not loading and no error */}
        {!loading && !error && transactionData && (
          <div className="grid grid-cols-1 gap-8">
            {/* Image + price cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <div className="w-full h-[243px] rounded-lg bg-gray-100 overflow-hidden">
                  {transactionData.item?.images?.length > 0 ? (
                    <img
                      src={
                        transactionData.item.images.find(
                          (img) => img.is_primary
                        )?.file_url ||
                        transactionData.item.images[0]?.file_url ||
                        "N/A"
                      }
                      alt={transactionData.item.title || "item"}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-500">
                      No Image Available
                    </div>
                  )}
                </div>
                <span
                  className={`absolute top-3 left-3 text-[14px] font-[600] px-2.5 py-1 rounded-full capitalize whitespace-nowrap leading-none ${statusBadge.className}`}
                >
                  {statusBadge?.text ? statusBadge.text : "N/A"}
                </span>
              </div>
              <div className="flex flex-row text-center md:flex-col w-full gap-4">
                <Card>
                  <p className="text-[14px] font-[600] text-[#1E1E1E] mb-4">
                    Sales price
                  </p>
                  <p className="text-[24px] font-[700] text-[#2A8BF7]">
                    {`${salePrice} €`}
                  </p>
                </Card>
                <Card>
                  <p className="text-[14px] font-[600] text-[#1E1E1E] mb-4">
                    In escrow
                  </p>
                  <p className="text-[24px] font-[700] text-[#2A8BF7]">
                    {escrowAmount} €
                  </p>
                </Card>
              </div>
            </div>

            <Card>
              <SectionTitle>Description</SectionTitle>
              <p className="text-[14px] opacity-50 mt-2">
                {transactionData.item?.description ||
                  "No description available."}
              </p>
            </Card>

            {/* Profile Overview */}
            <div
              className="relative overflow-hidden rounded-2xl p-5 text-white shadow-sm border border-white/20"
              style={{
                background:
                  "linear-gradient(57.04deg, #5A9AF8 0%, #2A8BF7 100%)",
              }}
            >
              <div
                className="absolute inset-0 left-[-110px] pointer-events-none"
                aria-hidden
              >
                <img
                  src={lineShape}
                  alt="ProtectPay"
                  className="w-full opacity-35 invert h-full"
                />
              </div>

              <div className="flex items-center justify-between mb-8 relative z-10">
                <h3 className="text-big-price font-rubik md:text-big-price-d !font-[500]">
                  Seller’s information
                </h3>
                <span className="bg-[#DCFCE7] text-[#16653F] border border-[#BBF7D0] text-[14px] font-semibold px-[10px] py-1 rounded-[50px] inline-flex items-center gap-1">
                  <FiCheck className="w-3.5 h-3.5" />
                  Verified
                </span>
              </div>

              <div className="flex items-center gap-4 relative z-10">
                {transactionData.seller?.profile_image_url ? (
                  <img
                    src={transactionData.seller.profile_image_url}
                    alt="seller"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-slate-600 rounded-full object-cover bg-slate-200 w-16 h-16 text-xl font-semibold flex items-center justify-center">
                    {transactionData.seller?.firstName?.charAt(0) || ""}
                    {transactionData.seller?.lastName?.charAt(0) || ""}
                  </span>
                )}
                <div className="flex-1">
                  <div className="text-small-price-d opacity-75 font-normal">
                    {transactionData.seller?.firstName &&
                    transactionData.seller?.lastName
                      ? `${transactionData.seller.firstName} ${transactionData.seller.lastName}`
                      : transactionData.seller?.email || "N/A"}
                  </div>
                  <div className="text-[12px] opacity-75">
                    {transactionData.seller?.email || "N/A"}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 text-xs relative z-10">
                <div>
                  <div className="text-white opacity-75 text-[12px]">
                    Total sales
                  </div>
                  <div className="text-small-price-d font-bold">
                    {sellerStats.totalSales ?? 0}
                  </div>
                </div>
                {/* <div className="text-center">
                  <div className="text-white opacity-75 text-[12px]">
                    Seller's rating
                  </div>
                  <div className="text-small-price-d font-bold inline-flex items-center gap-1 justify-center">
                    0
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="18"
                      viewBox="0 0 20 18"
                      fill="none"
                    >
                      <path
                        d="M10 0.5L12.1329 7.06434H19.035L13.4511 11.1213L15.584 17.6857L10 13.6287L4.41604 17.6857L6.54892 11.1213L0.964963 7.06434H7.86712L10 0.5Z"
                        fill="#FFF155"
                      />
                    </svg>
                  </div>
                </div> */}
                <div className="text-right">
                  <div className="text-white opacity-75 text-[12px]">
                    Total sold
                  </div>
                  <div className="text-small-price-d font-bold">
                    {sellerStats.totalSoldValue ?? 0} €
                  </div>
                </div>
              </div>

              <div className="flex mt-8 gap-4 flex-col items-center justify-between">
                <div className="w-full">
                  <div className="flex items-center gap-2 relative z-10">
                    {transactionData.seller_confirmed ? (
                      <button className="inline-flex w-full items-center justify-center gap-4 text-[#ffffff] text-subsection-title rounded-[8px] cursor-default">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="13"
                          viewBox="0 0 18 13"
                          fill="none"
                        >
                          <path
                            d="M17 1L6 12L1 7"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Confirmed by seller
                      </button>
                    ) : (
                      <button className="inline-flex w-full items-center justify-center gap-4 text-[#ffffff] text-subsection-title rounded-[8px] cursor-default">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M13 1L1 13M1 1L13 13"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Awaiting confirmation
                      </button>
                    )}
                  </div>
                </div>

                {!transactionData.seller_confirmed && (
                  <div className="w-full">
                    <div className="flex items-center gap-2 relative z-10">
                      <button
                        onClick={() => handleSendReminder("seller")}
                        disabled={sendingReminder === "seller"}
                        className={`inline-flex w-full items-center justify-center gap-4 text-subsection-title py-3 px-3 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed ${
                          sentReminder === "seller"
                            ? "bg-[#DCFCE7] text-[#16653F] border border-[#A0E8B8]"
                            : "bg-[#F5F8FF] text-[#2A8BF7]"
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M21.5 18L14.8571 12M9.14286 12L2.50003 18M2 7L10.1649 12.7154C10.8261 13.1783 11.1567 13.4097 11.5163 13.4993C11.8339 13.5785 12.1661 13.5785 12.4837 13.4993C12.8433 13.4097 13.1739 13.1783 13.8351 12.7154L22 7M6.8 20H17.2C18.8802 20 19.7202 20 20.362 19.673C20.9265 19.3854 21.3854 18.9265 21.673 18.362C22 17.7202 22 16.8802 22 15.2V8.8C22 7.11984 22 6.27976 21.673 5.63803C21.3854 5.07354 20.9265 4.6146 20.362 4.32698C19.7202 4 18.8802 4 17.2 4H6.8C5.11984 4 4.27976 4 3.63803 4.32698C3.07354 4.6146 2.6146 5.07354 2.32698 5.63803C2 6.27976 2 7.11984 2 8.8V15.2C2 16.8802 2 17.7202 2.32698 18.362C2.6146 18.9265 3.07354 19.3854 3.63803 19.673C4.27976 20 5.11984 20 6.8 20Z"
                            stroke={
                              sentReminder === "seller" ? "#16653F" : "#2A8BF7"
                            }
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {sendingReminder === "seller"
                          ? "Sending..."
                          : sentReminder === "seller"
                          ? "Email sent"
                          : "Send reminder email"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Profile Overview */}
            <div
              className="relative overflow-hidden rounded-2xl p-5 text-white shadow-sm border border-white/20"
              style={{
                background:
                  "linear-gradient(57.04deg, #5A9AF8 0%, #2A8BF7 100%)",
              }}
            >
              <div
                className="absolute inset-0 left-[-110px] pointer-events-none"
                aria-hidden
              >
                <img
                  src={lineShape}
                  alt="ProtectPay"
                  className="w-full opacity-35 invert h-full"
                />
              </div>

              <div className="flex items-center justify-between mb-8 relative z-10">
                <h3 className="text-big-price font-rubik md:text-big-price-d !font-[500]">
                  Buyer’s information
                </h3>
                <span className="bg-[#DCFCE7] text-[#16653F] border border-[#BBF7D0] text-[14px] font-semibold px-[10px] py-1 rounded-[50px] inline-flex items-center gap-1">
                  <FiCheck className="w-3.5 h-3.5" />
                  Verified
                </span>
              </div>

              <div className="flex items-center gap-4 relative z-10">
                {transactionData.buyer?.profile_image_url ? (
                  <img
                    src={transactionData.buyer.profile_image_url}
                    alt="buyer"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-slate-600 rounded-full object-cover bg-slate-200 w-16 h-16 text-xl font-semibold flex items-center justify-center">
                    {transactionData.buyer?.firstName?.charAt(0) || ""}
                    {transactionData.buyer?.lastName?.charAt(0) || ""}
                  </span>
                )}
                <div className="flex-1">
                  <div className="text-small-price-d opacity-75 font-normal">
                    {transactionData.buyer?.firstName &&
                    transactionData.buyer?.lastName
                      ? `${transactionData.buyer.firstName} ${transactionData.buyer.lastName}`
                      : transactionData.buyer?.email || "N/A"}
                  </div>
                  <div className="text-[12px] opacity-75">
                    {transactionData.buyer?.email || "N/A"}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 text-xs relative z-10">
                <div>
                  <div className="text-white opacity-75 text-[12px]">
                    Total purchases
                  </div>
                  <div className="text-small-price-d font-bold">
                    {buyerStats.totalPurchases ?? 0}
                  </div>
                </div>
                {/* <div className="text-center">
                  <div className="text-white opacity-75 text-[12px]">
                    Seller's rating
                  </div>
                  <div className="text-small-price-d font-bold inline-flex items-center gap-1 justify-center">
                    0
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="18"
                      viewBox="0 0 20 18"
                      fill="none"
                    >
                      <path
                        d="M10 0.5L12.1329 7.06434H19.035L13.4511 11.1213L15.584 17.6857L10 13.6287L4.41604 17.6857L6.54892 11.1213L0.964963 7.06434H7.86712L10 0.5Z"
                        fill="#FFF155"
                      />
                    </svg>
                  </div>
                </div> */}
                <div className="text-right">
                  <div className="text-white opacity-75 text-[12px]">
                    Trust score
                  </div>
                  <div className="text-small-price-d font-bold">
                    {buyerStats.trustScore ?? 0}
                  </div>
                </div>
              </div>

              <div className="flex mt-8 gap-4 flex-col items-center justify-between">
                <div className="w-full">
                  <div className="flex items-center gap-2 relative z-10">
                    {transactionData.buyer_confirmed ? (
                      <button className="inline-flex w-full items-center justify-center gap-4 text-[#ffffff] text-subsection-title rounded-[8px] cursor-default">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="13"
                          viewBox="0 0 18 13"
                          fill="none"
                        >
                          <path
                            d="M17 1L6 12L1 7"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Confirmed by buyer
                      </button>
                    ) : (
                      <button className="inline-flex w-full items-center justify-center gap-4 text-[#ffffff] text-subsection-title rounded-[8px] cursor-default">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Awaiting confirmation
                      </button>
                    )}
                  </div>
                </div>

                {!transactionData.buyer_confirmed && (
                  <div className="w-full">
                    <div className="flex items-center gap-2 relative z-10">
                      <button
                        onClick={() => handleSendReminder("buyer")}
                        disabled={sendingReminder === "buyer"}
                        className={`inline-flex w-full items-center justify-center gap-4 text-subsection-title py-3 px-3 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed ${
                          sentReminder === "buyer"
                            ? "bg-[#DCFCE7] text-[#16653F] border border-[#A0E8B8]"
                            : "bg-[#F5F8FF] text-[#2A8BF7]"
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M21.5 18L14.8571 12M9.14286 12L2.50003 18M2 7L10.1649 12.7154C10.8261 13.1783 11.1567 13.4097 11.5163 13.4993C11.8339 13.5785 12.1661 13.5785 12.4837 13.4993C12.8433 13.4097 13.1739 13.1783 13.8351 12.7154L22 7M6.8 20H17.2C18.8802 20 19.7202 20 20.362 19.673C20.9265 19.3854 21.3854 18.9265 21.673 18.362C22 17.7202 22 16.8802 22 15.2V8.8C22 7.11984 22 6.27976 21.673 5.63803C21.3854 5.07354 20.9265 4.6146 20.362 4.32698C19.7202 4 18.8802 4 17.2 4H6.8C5.11984 4 4.27976 4 3.63803 4.32698C3.07354 4.6146 2.6146 5.07354 2.32698 5.63803C2 6.27976 2 7.11984 2 8.8V15.2C2 16.8802 2 17.7202 2.32698 18.362C2.6146 18.9265 3.07354 19.3854 3.63803 19.673C4.27976 20 5.11984 20 6.8 20Z"
                            stroke={
                              sentReminder === "buyer" ? "#16653F" : "#2A8BF7"
                            }
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {sendingReminder === "buyer"
                          ? "Sending..."
                          : sentReminder === "buyer"
                          ? "Email sent"
                          : "Send reminder email"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </ModalContainer>
    </>
  );
};

export default TransactionPreviewModal;
